import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Wrench, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatUsd = (n) => {
  const val = Number(n) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
};

const inputClass = "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
const labelClass = "block text-xs font-['Manrope'] text-white/60 mb-1.5";

export default function ServiceQuoteApprovalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [fullName, setFullName] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/service-quote/${token}`)
      .then((res) => {
        setMeta(res.data);
        if (res.data.status === 'completed') {
          setSubmitted(true);
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'This quote link is invalid or expired');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error('You must agree to the quoted price to continue');
      return;
    }
    if (!fullName.trim()) {
      toast.error('Please enter your full legal name');
      return;
    }
    if (fullName.trim().split(/\s+/).length < 2) {
      toast.error('Please enter your full legal name (first and last name)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/service-quote/${token}/accept`, {
        consent: true,
        full_name: fullName.trim(),
      });
      setSubmitted(true);
      toast.success('Your approval has been recorded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit approval');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-white/40 font-['Manrope']">Loading service quote...</p>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <p className="text-white/60 font-['Manrope']">This quote link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  const quote = meta.quote || {};

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white/5 border border-white/10 p-8 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-xl font-['Unbounded'] font-bold text-white mb-2">Quote Approved</h1>
          <p className="text-sm font-['Manrope'] text-white/60">
            Thank you{meta.customer_consent_name ? `, ${meta.customer_consent_name}` : ''}.
            Drive Now Autohaus has received your approval and will proceed with your service.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-10 px-4" data-testid="service-quote-approval-page">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-blue-400 mb-2">
            <Wrench className="w-5 h-5" />
            <span className="text-sm font-['Unbounded'] font-semibold">Drive Now Autohaus</span>
          </div>
          <h1 className="text-xl font-['Unbounded'] font-bold text-white">Service Charge Quote</h1>
          <p className="text-sm font-['Manrope'] text-white/50 mt-1">
            Hello {meta.customer_name} — review your quote for {meta.vehicle_label}
          </p>
        </div>

        <div className="bg-white/5 border border-white/10 p-5 mb-6">
          <h2 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4">Parts</h2>
          <div className="space-y-2 mb-4">
            {(quote.parts || []).map((part, i) => (
              <div key={i} className="flex justify-between text-sm font-['Manrope']">
                <span className="text-white/70">{part.name}</span>
                <span className="text-white">{formatUsd(part.price)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-3 space-y-2 text-sm font-['Manrope']">
            <div className="flex justify-between text-white/60">
              <span>Labor ({quote.hours_required} hrs × {formatUsd(quote.hourly_labor_rate)}/hr)</span>
              <span className="text-white">{formatUsd(quote.total_labor_charge)}</span>
            </div>
            <div className="flex justify-between items-center pt-2">
              <span className="flex items-center gap-1.5 text-white font-semibold">
                <DollarSign className="w-4 h-4 text-blue-400" />
                Total Service Charge
              </span>
              <span className="text-lg font-['Unbounded'] font-bold text-white">
                {formatUsd(quote.total_service_charge)}
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 p-5 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 w-4 h-4 accent-blue-500"
              data-testid="quote-consent-checkbox"
            />
            <span className="text-sm font-['Manrope'] text-white/70 group-hover:text-white/90">
              I agree to the quoted price and would like the dealership to continue with the servicing procedure.
            </span>
          </label>

          {consent && (
            <div>
              <label className={labelClass}>Full Legal Name *</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full legal name to confirm"
                className={inputClass}
                data-testid="quote-full-name"
                autoFocus
              />
              <p className="text-[11px] font-['Manrope'] text-white/35 mt-1.5">
                Type your full legal name as you would when signing a document.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !consent || !fullName.trim()}
            data-testid="quote-approve-submit"
            className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting...' : 'Confirm & Approve Quote'}
          </button>
        </form>
      </div>
    </div>
  );
}